import {getRootUrl} from '@/constants/environment';
import {API_PATH_PREFIX} from '@/constants/path';
import PERMISSIONS from '@/constants/permissions';
import {removeCustomerFileRequest, removeLeadFileRequest} from '@/services/api/lead';
import {getAuthorizationHeader} from '@/services/auth';
import Paths from '@/services/paths';
import {FileSource, LeadModel} from '@/typings/models/Lead';
import {CheckAccess} from '@/utils/access';
import {URLParamBuilder} from '@/utils/functions';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {Badge, GetProp, Image, Popconfirm, Spin, Upload, UploadFile, UploadProps} from 'antd';
import React, {useState} from 'react';
import styles from './LeadFiles.less';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

type SourcedUploadFile = UploadFile & { source?: FileSource };

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const LeadFiles = ({
  leadDetails,
  refresh,
}: {
  refresh: () => void;
  leadDetails: LeadModel;
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);

  const handlePreview = async (file: UploadFile) => {
    if (file.preview) {
      setPreviewImage(file.preview as string);
      setPreviewOpen(true);
      return;
    }

    const fileName = file.name || file.url?.split('/').pop() || '';
    const extension = fileName.split('.').pop()?.toLowerCase();

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

    const isImage = extension && imageExtensions.includes(extension);

    if (isImage) {
      if (!file.url && file.originFileObj) {
        file.preview = await getBase64(file.originFileObj as FileType);
        setPreviewImage(file.preview as string);
      } else {
        setPreviewImage(file.url as string);
      }
      setPreviewOpen(true);
    } else {
      const fallbackUrl = file.url || file.response?.url;
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank');
      } else {
        console.warn('File not ready yet. Cannot preview or open.');
      }
    }
  };
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) =>
    // newFileList should be a set of uuid s
    setFileList(newFileList);

  const handleRemoveFile = (file: SourcedUploadFile) => {
    setLoading(true);
    const removeRequest =
      file.source === 'customer'
        ? removeCustomerFileRequest({ params: { customerFileId: file.uid } })
        : removeLeadFileRequest({ params: { leadFileId: file.uid } });
    removeRequest.then(() => {
      refresh();
      setLoading(false);
    });
  };
  const uploadButton = (
    <CheckAccess
      access={[PERMISSIONS.BUSINESS_PARTNER, PERMISSIONS.Leads.EDIT]}
    >
      <button
        className={styles.uploadButton}
        type="button"
      >
        <PlusOutlined />
        <div className={styles.uploadLabel}>
          <F id={'pages.images.upload'} defaultMessage={'Upload'} />
        </div>
      </button>
    </CheckAccess>
  );

  const uploadProps = {
    headers: getAuthorizationHeader(),
    action: URLParamBuilder(Paths.UPLOAD_FILE_FOR_LEAD, {
      params: { leadId: leadDetails.id },
      root: `${getRootUrl()}${API_PATH_PREFIX}`,
    }),
  };
  const mappedServerFiles: SourcedUploadFile[] = (leadDetails.files || []).map((file) => ({
    uid: file.uuid,
    source: 'user',
    status: 'done',
    url: file.path,
    fileName: file.path.split('/').pop() || '',
    name: file.path.split('/').pop() || '',
    thumbUrl: file.path,
  }));

  const mappedCustomerFiles: SourcedUploadFile[] = (leadDetails.customer_files || []).map(
    (file) => ({
      uid: file.uuid,
      source: 'customer',
      status: 'done',
      url: file.path,
      fileName: file.path.split('/').pop() || '',
      name: file.path.split('/').pop() || '',
      thumbUrl: file.path,
    }),
  );

  const mergedList = mappedServerFiles.concat(mappedCustomerFiles).concat(fileList);

  const fullFileList = Array.from(
    new Map(
      mergedList.map((file) => [file?.response?.data?.uuid || file.uid, file]),
    ).values(),
  );

  if (!leadDetails.id) return null;

  return (
    <Spin spinning={loading}>
      <Upload
        {...uploadProps}
        className={styles.filesUpload}
        listType="picture-card"
        multiple
        fileList={fullFileList}
        showUploadList={{
          showDownloadIcon: true,
          removeIcon: (file) => (
            <Popconfirm
              title={<F action={INTL_ACTION.REMOVE_CONFIRMATION} entity={INTL_ENTITY.FILE}/>}
              onConfirm={(e) => {
                e.stopPropagation();
                handleRemoveFile(file);
              }}
            >
              <DeleteOutlined />
            </Popconfirm>
          ),
        }}
        pastable
        // Removal is handled entirely by the Popconfirm in `removeIcon` (see
        // handleRemoveFile). Returning false prevents antd's own remove handler
        // from mutating the list on icon click, which would re-render the item
        // and tear down the just-opened Popconfirm — most visibly on customer
        // files wrapped in Badge.Ribbon.
        onRemove={() => false}
        itemRender={(originNode, file) =>
          (file as SourcedUploadFile).source === 'customer' ? (
            <Badge.Ribbon
              placement="start"
              color="blue"
              text={<F id="entity.customer.singular" defaultMessage="Customer" />}
            >
              {originNode}
            </Badge.Ribbon>
          ) : (
            originNode
          )
        }
        onPreview={handlePreview}
        onChange={handleChange}
      >
        {uploadButton}
      </Upload>
      {previewImage && (
        <Image
          wrapperClassName={styles.previewWrapperHidden}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </Spin>
  );
};

export default LeadFiles;
